import React, { useEffect } from 'react';
import { X, BarChart3, ArrowRight, Loader } from 'lucide-react';
// Recharts components now imported in individual tab components
import { useNavigate } from 'react-router-dom';
// axios and supabase now managed by hooks
import logger from '../utils/logger';

// Import Phase 1 & 2 components and hooks
import { useValuationAuth } from '../hooks/useValuationAuth';
import { useIndustrySearch } from '../hooks/useIndustrySearch';
import { useValuationFormState } from '../hooks/useValuationFormState';
import { useBusinessContext } from '../contexts/BusinessContext';
// IndustrySelector and MethodSelector now used within CalculationSettings
import { METHOD_DESCRIPTIONS } from './valuation/MethodSelector';
import { FIELD_HELP_CONTENT } from '../utils/valuationConstants';
import CalculationSettings from './valuation/CalculationSettings';
import IncomeStatementForm from './valuation/IncomeStatementForm';
import BalanceSheetForm from './valuation/BalanceSheetForm';
import CashFlowForm from './valuation/CashFlowForm';
import AddBacksForm from './valuation/AddBacksForm';
import RatiosTab from './valuation/RatiosTab';
import ChartsTab from './valuation/ChartsTab';
import ResultsTab from './valuation/ResultsTab';
// Auto-calculations and ratios now handled by individual tab components

const AdvancedValuationModal = ({ isOpen, onClose, onSubmit, valuationResult = null, error = '', loading = false }) => {
  const navigate = useNavigate();

  // Get business context
  const { currentBusinessId } = useBusinessContext();

  // Use Phase 1 & 2 hooks for state management
  const { getUserId, getAuthToken } = useValuationAuth();

  const {
    industries,
    selectedIndustry,
    setSelectedIndustry,
    selectedParent,
    setSelectedParent,
    industrySearch,
    searchResults,
    showSearchDropdown,
    loading: industryLoading,
    handleIndustrySearch,
    selectIndustryFromSearch,
    getSelectedIndustryDetails,
    setShowSearchDropdown
  } = useIndustrySearch();

  const {
    years,
    financialData,
    selectedMethod,
    privateDiscount,
    businessType,
    ccaMultipleType,
    capitalizationRate,
    capitalizationEarningsType,
    manualMultipleType,
    manualMultiple,
    methodInputs,
    activeTab,
    showMethodModal,
    showDiscountInfo,
    showHelpModal,
    showCalculationModal,
    helpContent,
    sliderCalculating,
    justRecalculated,
    contentScrollRef,
    debounceTimeout,
    setFinancialData,
    setSelectedMethod,
    setPrivateDiscount,
    setBusinessType,
    setCcaMultipleType,
    setCapitalizationRate,
    setCapitalizationEarningsType,
    setManualMultipleType,
    setManualMultiple,
    setMethodInputs,
    setActiveTab,
    setShowMethodModal,
    setShowDiscountInfo,
    setShowHelpModal,
    setShowCalculationModal,
    setHelpContent,
    setSliderCalculating,
    setJustRecalculated,
    handleInputChange,
    formatNumberWithCommas,
    parseNumberFromFormatted,
    apiLoading,
    apiError,
    lastSaved,
    hasLoadedFromAPI
  } = useValuationFormState(currentBusinessId);

  const currentYear = years[years.length - 1];

  // Remove duplicate methodDescriptions - now imported from MethodSelector
  const methodDescriptions = METHOD_DESCRIPTIONS;

  // fetchIndustries is now handled by useIndustrySearch hook

  // Industry search restoration is now handled by useIndustrySearch hook

  // Save financialData to localStorage whenever it changes (with user-specific key)
  useEffect(() => {
    const userId = getUserId();
    localStorage.setItem(`advancedValuationData_${userId}`, JSON.stringify(financialData));
  }, [financialData]);

  // Save method-specific inputs to localStorage
  useEffect(() => {
    const userId = getUserId();
    localStorage.setItem(`advancedValuationMethodInputs_${userId}`, JSON.stringify(methodInputs));
  }, [methodInputs]);

  // Save selectedParent to localStorage
  useEffect(() => {
    const userId = getUserId();
    if (selectedParent) localStorage.setItem(`advancedValuationSelectedParent_${userId}`, selectedParent);
  }, [selectedParent]);

  // Save other settings to localStorage
  useEffect(() => {
    const userId = getUserId();
    if (businessType) localStorage.setItem(`advancedValuationBusinessType_${userId}`, businessType);
  }, [businessType]);

  useEffect(() => {
    const userId = getUserId();
    if (capitalizationRate) localStorage.setItem(`advancedValuationCapRate_${userId}`, capitalizationRate);
  }, [capitalizationRate]);

  useEffect(() => {
    const userId = getUserId();
    localStorage.setItem(`advancedValuationCapEarningsType_${userId}`, capitalizationEarningsType);
  }, [capitalizationEarningsType]);

  useEffect(() => {
    const userId = getUserId();
    localStorage.setItem(`advancedValuationManualMultipleType_${userId}`, manualMultipleType);
  }, [manualMultipleType]);

  useEffect(() => {
    const userId = getUserId();
    if (manualMultiple) localStorage.setItem(`advancedValuationManualMultiple_${userId}`, manualMultiple);
  }, [manualMultiple]);

  useEffect(() => {
    const userId = getUserId();
    localStorage.setItem(`advancedValuationCcaMultipleType_${userId}`, ccaMultipleType);
  }, [ccaMultipleType]);

  // Auto-switch to Results tab when valuation completes
  useEffect(() => {
    if (valuationResult) {
      setActiveTab('results');
      // Scroll to top of content when switching to results
      if (contentScrollRef.current) {
        contentScrollRef.current.scrollTop = 0;
      }
    }
  }, [valuationResult]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  // Scroll to top whenever tab changes
  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Click outside handler for search dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = event.target.closest('.advanced-industry-search-container');
      if (!searchContainer && showSearchDropdown) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchDropdown]);

  // Auto-calculate SDE, add-backs total, and balance sheet totals
  useEffect(() => {
    const newData = { ...financialData };
    let updated = false;

    years.forEach(year => {
      // Calculate Gross Profit = Revenue - COGS
      if (financialData.revenue && financialData.cogs && financialData.gross_profit) {
        const revenue = parseFloat(parseNumberFromFormatted(financialData.revenue[year])) || 0;
        const cogs = parseFloat(parseNumberFromFormatted(financialData.cogs[year])) || 0;
        const calculatedGrossProfit = revenue - cogs;

        if (calculatedGrossProfit !== parseFloat(newData.gross_profit[year] || 0)) {
          newData.gross_profit[year] = calculatedGrossProfit.toString();
          updated = true;
        }
      }

      // Calculate EBITDA = Gross Profit - Operating Expenses
      if (financialData.gross_profit && financialData.operating_expenses && financialData.ebitda) {
        const grossProfit = parseFloat(newData.gross_profit[year]) || 0;
        const opex = parseFloat(parseNumberFromFormatted(financialData.operating_expenses[year])) || 0;
        const calculatedEBITDA = grossProfit - opex;

        if (calculatedEBITDA !== parseFloat(newData.ebitda[year] || 0)) {
          newData.ebitda[year] = calculatedEBITDA.toString();
          updated = true;
        }
      }

      // Calculate EBIT = EBITDA - Depreciation/Amortization
      if (financialData.ebitda && financialData.depreciation && financialData.ebit) {
        const ebitda = parseFloat(newData.ebitda[year]) || 0;
        const depreciation = parseFloat(parseNumberFromFormatted(financialData.depreciation[year])) || 0;
        const calculatedEBIT = ebitda - depreciation;

        if (calculatedEBIT !== parseFloat(newData.ebit[year] || 0)) {
          newData.ebit[year] = calculatedEBIT.toString();
          updated = true;
        }
      }

      // Calculate Net Income = EBIT - Interest Expense - Tax Expense
      if (financialData.ebit && financialData.interest_expense && financialData.tax_expense && financialData.profit_after_tax) {
        const ebit = parseFloat(newData.ebit[year]) || 0;
        const interestExpense = parseFloat(parseNumberFromFormatted(newData.interest_expense?.[year] || financialData.interest_expense[year])) || 0;
        const taxExpense = parseFloat(parseNumberFromFormatted(newData.tax_expense?.[year] || financialData.tax_expense[year])) || 0;
        const calculatedNetIncome = ebit - interestExpense - taxExpense;

        // Use a small tolerance for floating point comparison
        const currentNetIncome = parseFloat(newData.profit_after_tax[year] || 0);
        if (Math.abs(calculatedNetIncome - currentNetIncome) > 0.01) {
          newData.profit_after_tax[year] = calculatedNetIncome.toString();
          updated = true;
        }
      }

      // Calculate total add-backs (only if these fields exist)
      const addBackFields = [
        'owner_salary', 'family_salaries', 'salary_adjustments', 'auto_expenses',
        'donations', 'insurance_premiums', 'inventory_adjustments', 'meals_entertainment',
        'personal_expenses', 'phone_internet', 'non_business_services', 'travel',
        'one_time_charges', 'retirement', 'supplier_rebates', 'other_addbacks'
      ];

      const totalAddBacks = addBackFields.reduce((sum, field) => {
        // Check if field exists before accessing
        if (financialData[field] && financialData[field][year] !== undefined) {
          return sum + (parseFloat(parseNumberFromFormatted(financialData[field][year])) || 0);
        }
        return sum;
      }, 0);

      // Only update if addbacks_total field exists
      if (financialData.addbacks_total) {
        if (totalAddBacks !== parseFloat(newData.addbacks_total[year] || 0)) {
          newData.addbacks_total[year] = totalAddBacks.toString();
          updated = true;
        }
      }

      // Calculate SDE = EBITDA + Add-backs Total (only if fields exist)
      if (financialData.ebitda && financialData.sde) {
        const ebitda = parseFloat(financialData.ebitda[year]) || 0;
        const calculatedSDE = ebitda + totalAddBacks;

        if (calculatedSDE !== parseFloat(newData.sde[year] || 0)) {
          newData.sde[year] = calculatedSDE.toString();
          updated = true;
        }
      }

      // Calculate Total Assets (only if fields exist)
      if (financialData.cash && financialData.current_assets && financialData.non_current_assets && financialData.total_assets) {
        const cash = parseFloat(parseNumberFromFormatted(financialData.cash[year])) || 0;
        const currentAssets = parseFloat(parseNumberFromFormatted(financialData.current_assets[year])) || 0;
        const nonCurrentAssets = parseFloat(parseNumberFromFormatted(financialData.non_current_assets[year])) || 0;
        const totalAssets = cash + currentAssets + nonCurrentAssets;

        if (totalAssets !== parseFloat(newData.total_assets[year] || 0)) {
          newData.total_assets[year] = totalAssets.toString();
          updated = true;
        }
      }

      // Calculate Total Liabilities (only if fields exist)
      if (financialData.long_term_debt && financialData.short_term_debt && financialData.total_liabilities) {
        const longTermDebt = parseFloat(parseNumberFromFormatted(financialData.long_term_debt[year])) || 0;
        const shortTermDebt = parseFloat(parseNumberFromFormatted(financialData.short_term_debt[year])) || 0;
        const totalLiabilities = longTermDebt + shortTermDebt;

        if (totalLiabilities !== parseFloat(newData.total_liabilities[year] || 0)) {
          newData.total_liabilities[year] = totalLiabilities.toString();
          updated = true;
        }
      }

      // Calculate Total Equity = Total Assets - Total Liabilities (only if fields exist)
      if (financialData.total_assets && financialData.total_liabilities && financialData.equity) {
        const totalAssets = parseFloat(newData.total_assets[year]) || 0;
        const totalLiabilities = parseFloat(newData.total_liabilities[year]) || 0;
        const calculatedEquity = totalAssets - totalLiabilities;

        if (calculatedEquity !== parseFloat(newData.equity[year] || 0)) {
          newData.equity[year] = calculatedEquity.toString();
          updated = true;
        }
      }

      // Calculate Working Capital Change = (Current Assets - Current Liabilities) for current year minus previous year
      // Note: Current Liabilities field is named 'short_term_debt' in the Balance Sheet
      if (financialData.current_assets && financialData.short_term_debt && financialData.working_capital_change) {
        const currentAssetsVal = financialData.current_assets[year];
        const currentLiabilitiesVal = financialData.short_term_debt[year];

        const currentAssets = parseFloat(parseNumberFromFormatted(currentAssetsVal)) || 0;
        const currentLiabilities = parseFloat(parseNumberFromFormatted(currentLiabilitiesVal)) || 0;
        const currentWorkingCapital = currentAssets - currentLiabilities;

        // Get previous year's working capital
        const yearIndex = years.indexOf(year);
        if (yearIndex > 0) {
          const prevYear = years[yearIndex - 1];
          const prevCurrentAssetsVal = financialData.current_assets[prevYear];
          const prevCurrentLiabilitiesVal = financialData.short_term_debt[prevYear];

          const prevCurrentAssets = parseFloat(parseNumberFromFormatted(prevCurrentAssetsVal)) || 0;
          const prevCurrentLiabilities = parseFloat(parseNumberFromFormatted(prevCurrentLiabilitiesVal)) || 0;
          const prevWorkingCapital = prevCurrentAssets - prevCurrentLiabilities;

          // Change in WC = Current WC - Previous WC (negative = cash outflow, positive = cash inflow)
          const wcChange = currentWorkingCapital - prevWorkingCapital;
          const existingWcChange = parseFloat(parseNumberFromFormatted(newData.working_capital_change[year])) || 0;

          // Use tolerance for floating point comparison
          if (Math.abs(wcChange - existingWcChange) > 0.01) {
            newData.working_capital_change[year] = wcChange.toString();
            updated = true;
          }
        } else {
          // For the first year, set to 0 as there's no previous year to compare
          const existingWcChange = parseFloat(parseNumberFromFormatted(newData.working_capital_change[year])) || 0;
          if (Math.abs(existingWcChange) > 0.01) {
            newData.working_capital_change[year] = '0';
            updated = true;
          }
        }
      }

      // Calculate Free Cash Flow = Net Income + D&A - CapEx - Working Capital Change
      if (financialData.profit_after_tax && financialData.depreciation && financialData.capex &&
          financialData.working_capital_change && financialData.free_cash_flow) {
        const netIncome = parseFloat(newData.profit_after_tax[year]) || 0;
        const da = parseFloat(parseNumberFromFormatted(financialData.depreciation[year])) || 0;
        const capex = parseFloat(parseNumberFromFormatted(financialData.capex[year])) || 0;
        const wcChange = parseFloat(parseNumberFromFormatted(financialData.working_capital_change[year])) || 0;
        const calculatedFCF = netIncome + da - capex - wcChange;

        if (calculatedFCF !== parseFloat(newData.free_cash_flow[year] || 0)) {
          newData.free_cash_flow[year] = calculatedFCF.toString();
          updated = true;
        }
      }
    });

    if (updated) {
      setFinancialData(newData);
    }
  }, [
    financialData.revenue, financialData.cogs, financialData.operating_expenses,
    financialData.depreciation, financialData.tax_expense, financialData.interest_expense,
    financialData.ebitda, financialData.ebit, financialData.owner_salary, financialData.family_salaries,
    financialData.cash, financialData.current_assets, financialData.non_current_assets,
    financialData.long_term_debt, financialData.short_term_debt,
    financialData.equity, financialData.capex, financialData.working_capital_change
  ]);


  // showHelp function removed - now handled by individual components


  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const prepareChartData = () => {
    return years.map(year => ({
      year: year.toString(),
      'Revenue': parseFloat(financialData.revenue[year]) || 0,
      'Gross Profit': parseFloat(financialData.gross_profit[year]) || 0,
      'EBITDA': parseFloat(financialData.ebitda[year]) || 0,
      'SDE': parseFloat(financialData.sde[year]) || 0,
    }));
  };

  // Calculate financial ratios

  // calculateRatios function removed - now handled by RatiosTab component


  const handleClose = () => {
    setActiveTab('settings');
    onClose();
  };

  const handleSubmit = () => {
    const submissionData = {
      ...financialData,
      industry_id: selectedIndustry,
      private_company_discount: privateDiscount / 100,
      method: selectedMethod,
      cca_multiple_type: ccaMultipleType,
      manual_multiple_type: manualMultipleType,
      manual_multiple: parseFloat(manualMultiple) || 0,
      business_type: businessType,
      capitalization_rate: parseFloat(capitalizationRate) || 0,
      capitalization_earnings_type: capitalizationEarningsType,
      // Convert DCF parameters from percentages to decimals
      dcf_perpetuity: methodInputs.dcf_perpetuity,
      dcf_years: methodInputs.dcf_years,
      dcf_discount_rate: (methodInputs.dcf_discount_rate || 15) / 100,
      dcf_annual_growth_rate: (methodInputs.dcf_annual_growth_rate || 5) / 100,
      dcf_growth_rate: (methodInputs.dcf_growth_rate || 3) / 100
    };

    // Save settings to localStorage with user-specific keys
    const userId = getUserId();
    localStorage.setItem(`advancedValuationIndustry_${userId}`, selectedIndustry);
    localStorage.setItem(`advancedValuationDiscount_${userId}`, privateDiscount.toString());
    localStorage.setItem(`advancedValuationMethod_${userId}`, selectedMethod);

    onSubmit(submissionData);
  };

  const handleDiscountChange = (e) => {
    const newDiscount = parseInt(e.target.value);

    logger.debug('Advanced discount changed', { newDiscount, hasValuationResult: !!valuationResult });

    setPrivateDiscount(newDiscount);
    setJustRecalculated(false); // Reset the flag when slider changes

    // Save to localStorage immediately
    const userId = getUserId();
    localStorage.setItem(`advancedValuationDiscount_${userId}`, newDiscount.toString());

    // If we already have a result, recalculate with new discount
    if (valuationResult) {
      logger.debug('Triggering recalculation', { method: selectedMethod, discount: newDiscount });

      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      setSliderCalculating(true);

      debounceTimeout.current = setTimeout(() => {
        logger.debug('Performing calculation', { discount: newDiscount });
        performCalculation(newDiscount);
      }, 800);
    } else {
      logger.debug('Recalculation blocked - no existing result');
    }
  };

  const performCalculation = async (customDiscount = null) => {
    const discount = customDiscount !== null ? customDiscount : privateDiscount;

    try {
      const submissionData = {
        ...financialData,
        industry_id: selectedIndustry,
        private_company_discount: discount / 100,
        method: selectedMethod,
        cca_multiple_type: ccaMultipleType,
        manual_multiple_type: manualMultipleType,
        manual_multiple: parseFloat(manualMultiple) || 0,
        business_type: businessType,
        capitalization_rate: parseFloat(capitalizationRate) || 0,
        capitalization_earnings_type: capitalizationEarningsType,
        // Convert DCF parameters from percentages to decimals
        dcf_perpetuity: methodInputs.dcf_perpetuity,
        dcf_years: methodInputs.dcf_years,
        dcf_discount_rate: (methodInputs.dcf_discount_rate || 15) / 100,
        dcf_growth_rate: (methodInputs.dcf_growth_rate || 3) / 100
      };

      // Call the same onSubmit that handleSubmit uses - parent will handle API call and state update
      await onSubmit(submissionData);

      setSliderCalculating(false);
      setJustRecalculated(true); // Show success message
    } catch (error) {
      logger.error('Error recalculating with new discount', error);
      setSliderCalculating(false);
      setJustRecalculated(false);
    }
  };

  if (!isOpen) return null;


  // chartData, selectedIndustryData, and HelpTooltip removed - now handled by tab components

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex items-center gap-3">
            <BarChart3 className="text-white" size={28} />
            <div>
              <h2 className="text-2xl font-bold text-white">Advanced Financial Analysis</h2>
              <p className="text-blue-100 text-sm">Multi-year detailed valuation</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-white hover:bg-white/20 p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center">
            <X size={24} />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-6 mt-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex overflow-x-auto">
            {['settings', 'income', 'balance', 'cashflow', 'addbacks', 'ratios', 'charts', 'results'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${activeTab === tab
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:bg-gray-100'
                  }${tab === 'results' ? ' bg-green-50 text-green-700 hover:bg-green-100' : ''}`}
              >
                {tab === 'settings' && '⚙️ Settings'}
                {tab === 'income' && '📊 Income'}
                {tab === 'balance' && '🏦 Balance'}
                {tab === 'cashflow' && '💰 Cash Flow'}
                {tab === 'addbacks' && '➕ Add-backs'}
                {tab === 'ratios' && '🔢 Ratios'}
                {tab === 'charts' && '📈 Charts'}
                {tab === 'results' && '✅ Results'}
              </button>
            ))}
          </div>
        </div>

        <div ref={contentScrollRef} className="flex-1 overflow-y-auto p-6">
          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <CalculationSettings
              selectedMethod={selectedMethod}
              setSelectedMethod={setSelectedMethod}
              onShowMethodDetails={(methodKey) => {
                setHelpContent({
                  title: METHOD_DESCRIPTIONS[methodKey].name,
                  content: `${METHOD_DESCRIPTIONS[methodKey].description}\n\nPros: ${METHOD_DESCRIPTIONS[methodKey].pros}\n\nCons: ${METHOD_DESCRIPTIONS[methodKey].cons}\n\nBest for: ${METHOD_DESCRIPTIONS[methodKey].bestFor}`
                });
                setShowMethodModal(true);
              }}
              onShowHelp={(fieldKey) => {
                setHelpContent(FIELD_HELP_CONTENT[fieldKey]);
                setShowHelpModal(true);
              }}
              // Industry selection props
              industries={industries}
              selectedParent={selectedParent}
              setSelectedParent={setSelectedParent}
              selectedIndustry={selectedIndustry}
              setSelectedIndustry={setSelectedIndustry}
              industrySearch={industrySearch}
              searchResults={searchResults}
              showSearchDropdown={showSearchDropdown}
              onIndustrySearchChange={handleIndustrySearch}
              onSelectIndustry={selectIndustryFromSearch}
              onDropdownClose={() => setShowSearchDropdown(false)}
              industryLoading={industryLoading}
              // Method configuration
              ccaMultipleType={ccaMultipleType}
              setCcaMultipleType={setCcaMultipleType}
              manualMultipleType={manualMultipleType}
              setManualMultipleType={setManualMultipleType}
              manualMultiple={manualMultiple}
              setManualMultiple={setManualMultiple}
              privateDiscount={privateDiscount}
              setPrivateDiscount={setPrivateDiscount}
              onShowDiscountInfo={() => setShowDiscountInfo(true)}
              methodInputs={methodInputs}
              setMethodInputs={setMethodInputs}
              capitalizationEarningsType={capitalizationEarningsType}
              setCapitalizationEarningsType={setCapitalizationEarningsType}
              businessType={businessType}
              setBusinessType={setBusinessType}
              capitalizationRate={capitalizationRate}
              setCapitalizationRate={setCapitalizationRate}
              // Financial data
              financialData={financialData}
              currentYear={currentYear}
              formatNumberWithCommas={formatNumberWithCommas}
              selectedIndustryDetails={getSelectedIndustryDetails()}
            />
          )}

          {/* Income Statement Tab */}
          {activeTab === 'income' && (
            <IncomeStatementForm
              years={years}
              financialData={financialData}
              formatNumberWithCommas={formatNumberWithCommas}
              handleInputChange={handleInputChange}
            />
          )}

          {/* Balance Sheet Tab */}
          {activeTab === 'balance' && (
            <BalanceSheetForm
              years={years}
              financialData={financialData}
              formatNumberWithCommas={formatNumberWithCommas}
              handleInputChange={handleInputChange}
            />
          )}

          {/* Cash Flow Tab */}
          {activeTab === 'cashflow' && (
            <CashFlowForm
              years={years}
              financialData={financialData}
              formatNumberWithCommas={formatNumberWithCommas}
              handleInputChange={handleInputChange}
            />
          )}

          {/* Add-backs Tab */}
          {activeTab === 'addbacks' && (
            <AddBacksForm
              years={years}
              financialData={financialData}
              formatNumberWithCommas={formatNumberWithCommas}
              handleInputChange={handleInputChange}
              parseNumberFromFormatted={parseNumberFromFormatted}
            />
          )}

          {/* Ratios Tab */}
          {activeTab === 'ratios' && (
            <RatiosTab
              years={years}
              financialData={financialData}
            />
          )}
          {/* Charts Tab */}
          {activeTab === 'charts' && (
            <ChartsTab
              years={years}
              financialData={financialData}
              formatCurrency={formatCurrency}
            />
          )}

          {/* Results Tab */}
          {activeTab === 'results' && (
            <ResultsTab
              valuationResult={valuationResult}
              privateDiscount={privateDiscount}
              handleDiscountChange={handleDiscountChange}
              sliderCalculating={sliderCalculating}
              justRecalculated={justRecalculated}
              setShowCalculationModal={setShowCalculationModal}
              setShowDiscountInfo={setShowDiscountInfo}
              navigate={navigate}
              onClose={onClose}
            />
          )}
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
          <button
            onClick={handleClose}
            className="px-6 py-2 border rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <div className="flex items-center gap-4">
            {!selectedIndustry && (
              <span className="text-sm text-red-600">⚠️ Please select an industry</span>
            )}
            <button
              onClick={handleSubmit}
              disabled={!selectedIndustry || loading}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 min-h-[44px] ${
                selectedIndustry && !loading
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  Calculating...
                </>
              ) : (
                <>
                  <BarChart3 size={20} />
                  Calculate Advanced Valuation
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Method Descriptions Modal */}
      {showMethodModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Valuation Methods Explained</h2>
                <button onClick={() => setShowMethodModal(false)} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <X size={24} />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Click any method below to select it for your valuation.
              </p>

              <div className="space-y-6">
                {Object.entries(methodDescriptions).map(([key, method]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setSelectedMethod(key);
                      const userId = getUserId();
                      localStorage.setItem(`advancedValuationMethod_${userId}`, key);
                      setShowMethodModal(false);
                    }}
                    className="w-full text-left border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <span className="text-3xl">{method.icon}</span>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{method.name}</h3>
                        <p className="text-sm text-gray-700 mt-1">{method.description}</p>
                      </div>
                      <div className="flex items-center gap-2 text-blue-600 font-medium text-sm">
                        Select
                        <ArrowRight size={16} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-1">✓ PROS</p>
                        <p className="text-sm text-gray-600">{method.pros}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-orange-700 mb-1">✗ CONS</p>
                        <p className="text-sm text-gray-600">{method.cons}</p>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-semibold text-blue-700 mb-1">BEST FOR</p>
                      <p className="text-sm text-gray-700">{method.bestFor}</p>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={() => setShowMethodModal(false)}
                className="mt-6 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors min-h-[44px]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Private Company Discount Modal */}
      {showDiscountInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Private Company Discount</h2>
                <button onClick={() => setShowDiscountInfo(false)} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <X size={24} />
                </button>
              </div>

              <div className="prose prose-blue max-w-none">
                <p className="text-gray-700 mb-4">
                  <strong>Private Company Discount</strong> (also called "Marketability Discount" or "Illiquidity Discount") reflects the fact that private company shares are harder to sell than public company shares.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Why Apply a Discount?</h3>
                <ul className="space-y-2 mb-6">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <div>
                      <strong>Limited Liquidity:</strong> Private shares can't be quickly sold on a public exchange
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <div>
                      <strong>Smaller Buyer Pool:</strong> Fewer potential buyers compared to public stocks
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <div>
                      <strong>Information Asymmetry:</strong> Less financial transparency than public companies
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <div>
                      <strong>Control Factors:</strong> Minority stakes may have limited influence
                    </div>
                  </li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Typical Discount Ranges</h3>
                <div className="space-y-3 mb-6">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-semibold text-green-900">0-15% Discount</p>
                    <p className="text-sm text-green-700">Strong growth, clear exit path, strategic buyers interested</p>
                  </div>

                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="font-semibold text-blue-900">20-30% Discount (Standard)</p>
                    <p className="text-sm text-blue-700">Typical private company, stable operations, normal liquidity constraints</p>
                  </div>

                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="font-semibold text-orange-900">35-50% Discount</p>
                    <p className="text-sm text-orange-700">Limited growth, owner-dependent, difficult to sell, minority stake</p>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">How to Choose Your Discount</h3>
                <p className="text-gray-700 mb-4">Consider these factors:</p>
                <ul className="space-y-2 mb-6 list-disc list-inside">
                  <li className="text-gray-700"><strong>Financial Performance:</strong> Stronger = lower discount</li>
                  <li className="text-gray-700"><strong>Management Team:</strong> Less owner-dependent = lower discount</li>
                  <li className="text-gray-700"><strong>Market Position:</strong> Strategic value = lower discount</li>
                  <li className="text-gray-700"><strong>Size:</strong> Larger companies often get lower discounts</li>
                  <li className="text-gray-700"><strong>Exit Readiness:</strong> More prepared = lower discount</li>
                </ul>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-6">
                  <p className="text-sm text-purple-900">
                    <strong>💡 Recommendation:</strong> The standard 25% discount is a safe starting point. Adjust based on your business's specific strengths and weaknesses. A well-prepared business with strong growth can justify a 15-20% discount, while a struggling or owner-dependent business might warrant 30-40%.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowDiscountInfo(false)}
                className="mt-6 w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors min-h-[44px]"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generic Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{helpContent.title}</h2>
                <button onClick={() => setShowHelpModal(false)} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <X size={24} />
                </button>
              </div>

              <div className="prose prose-blue max-w-none">
                <div className="text-gray-700 whitespace-pre-line">
                  {helpContent.content}
                </div>
              </div>

              <button
                onClick={() => setShowHelpModal(false)}
                className="mt-6 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors min-h-[44px]"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calculation Details Modal */}
      {showCalculationModal && valuationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 flex justify-between items-center shadow-lg rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Calculation Details</h3>
                  <p className="text-blue-100 text-sm">
                    {valuationResult.selected_method === 'cca' && 'Comparable Company Analysis'}
                    {valuationResult.selected_method === 'dcf' && 'Discounted Cash Flow'}
                    {valuationResult.selected_method === 'capitalization' && 'Capitalization of Earnings'}
                    {valuationResult.selected_method === 'nav' && 'Net Asset Value'}
                    {valuationResult.selected_method === 'manual' && 'Manual Multiple'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCalculationModal(false)}
                className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Method Content Wrapper - matches Simple Calculator structure */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  {/* Content */}
                  <div className="p-6 space-y-6">

                    {/* CCA Method */}
                    {valuationResult.selected_method === 'cca' && (
                      <div className="space-y-4">
                        {/* Input Parameters */}
                        <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-5 rounded-lg border-2 border-blue-400 shadow-md">
                          <p className="text-base font-bold text-blue-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Input Parameters
                          </p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Primary Metric</p>
                              <p className="font-bold text-blue-900">{ccaMultipleType === 'ev_ebitda' ? 'EBITDA' : 'Revenue'}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Multiple Type</p>
                              <p className="font-bold text-blue-900">{ccaMultipleType === 'ev_ebitda' ? 'EV/EBITDA' : 'EV/REVENUE'}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Revenue</p>
                              <p className="font-bold text-blue-900">${Math.round(valuationResult.financial_summary?.current_year?.revenue || 0).toLocaleString()}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">EBITDA</p>
                              <p className="font-bold text-blue-900">${Math.round(valuationResult.financial_summary?.current_year?.ebitda || 0).toLocaleString()}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded col-span-2">
                              <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                              <p className="font-bold text-amber-700">{valuationResult.industry_info?.private_discount_applied?.toFixed(0) || 0}%</p>
                            </div>
                          </div>
                        </div>

                        {/* CCA Step-by-Step Calculation */}
                        <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                          <p className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            Step 1: {ccaMultipleType === 'ev_ebitda' ? 'EV/EBITDA' : 'EV/REVENUE'} Valuation
                          </p>

                          <div className="space-y-3">
                            {/* Industry Multiple */}
                            <div className="bg-blue-50 p-3 rounded">
                              <p className="text-xs text-gray-600 mb-1">Industry Multiple</p>
                              <p className="font-mono text-blue-700 font-bold text-base">
                                {ccaMultipleType === 'ev_ebitda'
                                  ? valuationResult.industry_info?.original_multiples?.ev_ebitda?.toFixed(2)
                                  : valuationResult.industry_info?.original_multiples?.ev_revenue?.toFixed(2)}x
                              </p>
                            </div>

                            {/* Calculation Steps */}
                            <div className="bg-gray-50 p-3 rounded space-y-2">
                              <p className="text-xs font-semibold text-gray-700">Calculation:</p>

                              {/* Step 1: Base Valuation */}
                              <div className="font-mono text-xs">
                                <p className="text-gray-700">Base Value = {ccaMultipleType === 'ev_ebitda' ? 'EBITDA' : 'Revenue'} × Industry Multiple</p>
                                <p className="text-gray-900 mt-1">
                                  Base Value = ${Math.round(ccaMultipleType === 'ev_ebitda'
                                    ? valuationResult.financial_summary?.current_year?.ebitda
                                    : valuationResult.financial_summary?.current_year?.revenue || 0).toLocaleString()} × {ccaMultipleType === 'ev_ebitda'
                                    ? valuationResult.industry_info?.original_multiples?.ev_ebitda?.toFixed(2)
                                    : valuationResult.industry_info?.original_multiples?.ev_revenue?.toFixed(2)}x
                                </p>
                                <p className="text-blue-700 font-bold mt-1">
                                  Base Value = ${Math.round((ccaMultipleType === 'ev_ebitda'
                                    ? valuationResult.financial_summary?.current_year?.ebitda
                                    : valuationResult.financial_summary?.current_year?.revenue || 0) * (ccaMultipleType === 'ev_ebitda'
                                    ? valuationResult.industry_info?.original_multiples?.ev_ebitda
                                    : valuationResult.industry_info?.original_multiples?.ev_revenue || 1)).toLocaleString()}
                                </p>
                              </div>

                              {/* Step 2: Apply Discount if exists */}
                              {valuationResult.industry_info && valuationResult.industry_info.private_discount_applied > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <div className="font-mono text-xs space-y-1">
                                    <p className="text-amber-700 font-semibold">Apply Private Company Discount ({valuationResult.industry_info.private_discount_applied.toFixed(0)}%):</p>
                                    <p className="text-gray-700">
                                      Adjustment Multiplier = 1 - {(valuationResult.industry_info.private_discount_applied / 100).toFixed(3)} = {(1 - valuationResult.industry_info.private_discount_applied / 100).toFixed(3)}
                                    </p>
                                    <p className="text-gray-900 mt-1">
                                      Adjusted Value = ${Math.round((ccaMultipleType === 'ev_ebitda'
                                        ? valuationResult.financial_summary?.current_year?.ebitda
                                        : valuationResult.financial_summary?.current_year?.revenue || 0) * (ccaMultipleType === 'ev_ebitda'
                                        ? valuationResult.industry_info?.original_multiples?.ev_ebitda
                                        : valuationResult.industry_info?.original_multiples?.ev_revenue || 1)).toLocaleString()} × {(1 - valuationResult.industry_info.private_discount_applied / 100).toFixed(3)}
                                    </p>
                                    <p className="text-amber-700 font-bold mt-1">
                                      Adjusted Value = ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Valuation Range */}
                            {valuationResult.low_range && valuationResult.high_range && (
                              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border-l-4 border-green-500">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <p className="text-xs text-gray-600 mb-1">Valuation Range</p>
                                    <p className="font-bold text-green-700">
                                      ${Math.round(valuationResult.low_range).toLocaleString()} - ${Math.round(valuationResult.high_range).toLocaleString()}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-600 mb-1">Final Value</p>
                                    <p className="font-bold text-blue-700 text-base">
                                      ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* DCF Method */}
                    {valuationResult.selected_method === 'dcf' && (
                      <div className="space-y-4">
                        {/* Input Parameters */}
                        <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-5 rounded-lg border-2 border-blue-400 shadow-md">
                          <p className="text-base font-bold text-blue-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Input Parameters
                          </p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Free Cash Flow (CF₀)</p>
                              <p className="font-bold text-blue-900">${Math.round(valuationResult.financial_summary?.current_year?.sde || 0).toLocaleString()}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Discount Rate (r)</p>
                              <p className="font-bold text-blue-900">{methodInputs.dcf_discount_rate || 15}%</p>
                            </div>
                            {methodInputs.dcf_perpetuity ? (
                              <>
                                <div className="bg-white/70 p-2 rounded">
                                  <p className="text-xs text-gray-600 mb-1">Terminal Growth Rate (g)</p>
                                  <p className="font-bold text-blue-900">{methodInputs.dcf_growth_rate || 3}%</p>
                                </div>
                                <div className="bg-white/70 p-2 rounded">
                                  <p className="text-xs text-gray-600 mb-1">Model Type</p>
                                  <p className="font-bold text-blue-900">Gordon Growth</p>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="bg-white/70 p-2 rounded">
                                  <p className="text-xs text-gray-600 mb-1">Projection Period</p>
                                  <p className="font-bold text-blue-900">{valuationResult.method_details?.projection_years || methodInputs.dcf_years || 5} years</p>
                                </div>
                                <div className="bg-white/70 p-2 rounded">
                                  <p className="text-xs text-gray-600 mb-1">Annual Growth Rate</p>
                                  <p className="font-bold text-blue-900">{methodInputs.dcf_annual_growth_rate || 5}%</p>
                                </div>
                                <div className="bg-white/70 p-2 rounded">
                                  <p className="text-xs text-gray-600 mb-1">Terminal Growth Rate (g)</p>
                                  <p className="font-bold text-blue-900">{methodInputs.dcf_growth_rate || 3}%</p>
                                </div>
                                <div className="bg-white/70 p-2 rounded">
                                  <p className="text-xs text-gray-600 mb-1">Model Type</p>
                                  <p className="font-bold text-blue-900">Multi-Period DCF</p>
                                </div>
                              </>
                            )}
                            <div className="bg-white/70 p-2 rounded col-span-2">
                              <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                              <p className="font-bold text-amber-700">{valuationResult.industry_info?.private_discount_applied?.toFixed(0) || 0}%</p>
                            </div>
                          </div>
                        </div>

                        {/* Gordon Growth Model Calculation */}
                        {methodInputs.dcf_perpetuity && valuationResult.method_details?.calculation_steps?.gordon_growth_calculation && (
                          <div className="bg-white p-3 rounded border border-blue-100">
                            <p className="text-xs font-semibold text-blue-800 mb-2">Gordon Growth Model Calculation:</p>
                            <div className="space-y-1 text-sm font-mono">
                              <p className="text-gray-700">Step 1: Calculate next year's cash flow (CF₁)</p>
                              <p className="text-gray-900 ml-4">CF₁ = CF₀ × (1 + g)</p>
                              <p className="text-gray-900 ml-4">CF₁ = ${valuationResult.method_details.calculation_steps.gordon_growth_calculation.cf0?.toLocaleString()} × (1 + {((methodInputs.dcf_growth_rate || 3) / 100).toFixed(4)})</p>
                              <p className="text-blue-700 font-semibold ml-4">CF₁ = ${valuationResult.method_details.calculation_steps.gordon_growth_calculation.cf1?.toLocaleString()}</p>

                              <p className="text-gray-700 mt-3">Step 2: Calculate Enterprise Value</p>
                              <p className="text-gray-900 ml-4">Enterprise Value = CF₁ / (r - g)</p>
                              <p className="text-gray-900 ml-4">Enterprise Value = ${valuationResult.method_details.calculation_steps.gordon_growth_calculation.cf1?.toLocaleString()} / ({((methodInputs.dcf_discount_rate || 15) / 100).toFixed(4)} - {((methodInputs.dcf_growth_rate || 3) / 100).toFixed(4)})</p>

                              {valuationResult.industry_info && valuationResult.industry_info.private_discount_applied > 0 ? (
                                <>
                                  <p className="text-blue-700 font-semibold ml-4 mt-1">Base Enterprise Value = ${Math.round(valuationResult.valuation_amount / (1 - valuationResult.industry_info.private_discount_applied / 100)).toLocaleString()}</p>
                                  <p className="text-amber-700 ml-4 mt-2">Apply {valuationResult.industry_info.private_discount_applied.toFixed(0)}% Private Company Discount:</p>
                                  <p className="text-green-700 font-bold ml-4">Final Enterprise Value = ${Math.round(valuationResult.valuation_amount).toLocaleString()}</p>
                                </>
                              ) : (
                                <p className="text-green-700 font-bold ml-4 mt-1">Enterprise Value = ${valuationResult.method_details.calculation_steps.gordon_growth_calculation.enterprise_value?.toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Multi-Year Projection Calculations */}
                        {!methodInputs.dcf_perpetuity && valuationResult.method_details?.calculation_steps?.yearly_calculations && (
                          <>
                            {/* Step 1: Project Explicit Period FCFs */}
                            <div className="bg-white p-3 rounded border border-blue-100">
                              <p className="text-sm font-bold text-blue-800 mb-3">Step 1: Project Explicit Period FCFs (Years 1–{valuationResult.method_details.projection_years})</p>
                              <p className="text-xs text-gray-700 mb-2">Formula for each year (input FCF represents Year 0/today):</p>
                              <p className="text-xs font-mono text-gray-900 mb-3">FCF_t = FCF_0 × (1 + explicit growth)^t</p>
                              <div className="space-y-2 text-xs">
                                {valuationResult.method_details.calculation_steps.yearly_calculations.map((calc) => (
                                  <div key={calc.year} className="bg-gray-50 p-2 rounded">
                                    <p className="font-semibold text-gray-900">Year {calc.year}:</p>
                                    <p className="font-mono text-gray-700">
                                      FCF_{calc.year} = ${valuationResult.method_details.calculation_steps.base_fcf?.toLocaleString()} × (1 + {(calc.growth_rate * 100).toFixed(0)}%)^{calc.exponent} = ${valuationResult.method_details.calculation_steps.base_fcf?.toLocaleString()} × {calc.growth_multiplier?.toFixed(6)} = <span className="font-bold">${Math.round(calc.cash_flow).toLocaleString()}</span>
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Step 2: Calculate PV of Each Explicit FCF */}
                            <div className="bg-white p-3 rounded border border-blue-100">
                              <p className="text-sm font-bold text-blue-800 mb-3">Step 2: Calculate PV of Each Explicit FCF</p>
                              <p className="text-xs text-gray-700 mb-2">Discount each FCF back to today using:</p>
                              <p className="text-xs font-mono text-gray-900 mb-3">PV_t = FCF_t / (1 + r)^t</p>
                              <div className="space-y-2 text-xs">
                                {valuationResult.method_details.calculation_steps.yearly_calculations.map((calc) => (
                                  <div key={calc.year} className="bg-gray-50 p-2 rounded">
                                    <p className="font-semibold text-gray-900">Year {calc.year} PV:</p>
                                    <p className="font-mono text-gray-700">
                                      PV_{calc.year} = ${Math.round(calc.cash_flow).toLocaleString()} / (1 + {(valuationResult.method_details.discount_rate * 100).toFixed(0)}%)^{calc.year} = ${Math.round(calc.cash_flow).toLocaleString()} / {calc.discount_factor?.toFixed(calc.year <= 2 ? 2 : 6)} = <span className="font-bold">${Math.round(calc.present_value).toLocaleString()}</span>
                                    </p>
                                  </div>
                                ))}
                                <div className="bg-blue-50 p-3 rounded border border-blue-200 mt-3">
                                  <p className="font-bold text-blue-900 text-sm">Sum of PVs for Explicit Period:</p>
                                  <p className="font-mono text-blue-700 text-xs">
                                    {valuationResult.method_details.calculation_steps.yearly_calculations.map(calc => `$${Math.round(calc.present_value).toLocaleString()}`).join(' + ')} = <span className="font-bold">${Math.round(valuationResult.method_details?.calculation_steps?.final_calculation?.sum_of_pv_projections).toLocaleString()}</span>
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Step 3: Calculate Terminal Value */}
                            {valuationResult.method_details?.calculation_steps?.terminal_value_calculation && (
                              <div className="bg-white p-3 rounded border border-blue-100">
                                <p className="text-sm font-bold text-blue-800 mb-3">Step 3: Calculate Terminal Value (TV) at End of Year {valuationResult.method_details.projection_years}</p>
                                <div className="space-y-3 text-xs">
                                  <div>
                                    <p className="text-gray-700 mb-1">First, project FCF for Year {valuationResult.method_details.projection_years + 1} (using terminal growth):</p>
                                    <p className="font-mono text-gray-900 bg-gray-50 p-2 rounded text-xs">
                                      FCF_{valuationResult.method_details.projection_years + 1} = FCF_{valuationResult.method_details.projection_years} × (1 + g) = ${Math.round(valuationResult.method_details.calculation_steps.terminal_value_calculation.last_year_fcf).toLocaleString()} × (1 + {(valuationResult.method_details.terminal_growth * 100).toFixed(0)}%) = <span className="font-bold">${Math.round(valuationResult.method_details.calculation_steps.terminal_value_calculation.terminal_cash_flow).toLocaleString()}</span>
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-700 mb-1">Then, apply Gordon Growth Model for perpetuity:</p>
                                    <p className="font-mono text-gray-900 bg-gray-50 p-2 rounded text-xs">
                                      TV_{valuationResult.method_details.projection_years} = FCF_{valuationResult.method_details.projection_years + 1} / (r - g) = ${Math.round(valuationResult.method_details.calculation_steps.terminal_value_calculation.terminal_cash_flow).toLocaleString()} / ({(valuationResult.method_details.discount_rate * 100).toFixed(0)}% - {(valuationResult.method_details.terminal_growth * 100).toFixed(0)}%) = <span className="font-bold">${Math.round(valuationResult.method_details.calculation_steps.terminal_value_calculation.terminal_value).toLocaleString()}</span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Step 4: Discount Terminal Value to Present */}
                            {valuationResult.method_details?.calculation_steps?.terminal_value_calculation && (
                              <div className="bg-white p-3 rounded border border-blue-100">
                                <p className="text-sm font-bold text-blue-800 mb-3">Step 4: Discount Terminal Value to Present</p>
                                <div className="text-xs">
                                  <p className="font-mono text-gray-900 bg-gray-50 p-2 rounded text-xs">
                                    PV_terminal = TV_{valuationResult.method_details.projection_years} / (1 + r)^{valuationResult.method_details.projection_years} = ${Math.round(valuationResult.method_details.calculation_steps.terminal_value_calculation.terminal_value).toLocaleString()} / (1 + {(valuationResult.method_details.discount_rate * 100).toFixed(0)}%)^{valuationResult.method_details.projection_years} = <span className="font-bold">${Math.round(valuationResult.method_details.calculation_steps.terminal_value_calculation.terminal_pv).toLocaleString()}</span>
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Step 5: Total Enterprise Value */}
                            {valuationResult.method_details?.calculation_steps?.final_calculation && (
                              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border-l-4 border-green-500 shadow-sm">
                                <p className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Step 5: Total Enterprise Value (EV)
                                </p>
                                <div className="space-y-2 text-xs">
                                  <p className="text-gray-700">Sum the PV of explicit period and PV of terminal:</p>
                                  <p className="font-mono text-gray-900 bg-white/70 p-2 rounded">
                                    EV = PV_explicit + PV_terminal<br />
                                    EV = ${Math.round(valuationResult.method_details.calculation_steps.final_calculation.sum_of_pv_projections).toLocaleString()} + ${Math.round(valuationResult.method_details.calculation_steps.final_calculation.pv_of_terminal).toLocaleString()}<br />
                                    <span className="font-bold text-green-700 text-base">EV = ${Math.round(valuationResult.method_details.calculation_steps.final_calculation.enterprise_value).toLocaleString()}</span>
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Private Company Discount Application */}
                            {valuationResult.industry_info && valuationResult.industry_info.private_discount_applied > 0 && (
                              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg border-l-4 border-amber-500 shadow-sm">
                                <p className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Final Step: Apply Private Company Discount
                                </p>
                                <div className="space-y-2 text-xs">
                                  <p className="text-gray-700">Private companies typically trade at a discount to public companies due to illiquidity and information asymmetry.</p>
                                  <div className="bg-white/70 p-3 rounded">
                                    <p className="font-mono text-gray-900 text-xs">
                                      Base Enterprise Value = ${Math.round(valuationResult.method_details.calculation_steps.final_calculation.enterprise_value).toLocaleString()}
                                    </p>
                                    <p className="font-mono text-amber-700 text-xs mt-2">
                                      Discount Factor = {valuationResult.industry_info.private_discount_applied.toFixed(0)}% = {(valuationResult.industry_info.private_discount_applied / 100).toFixed(3)}
                                    </p>
                                    <p className="font-mono text-amber-700 text-xs mt-1">
                                      Adjustment Multiplier = 1 - {(valuationResult.industry_info.private_discount_applied / 100).toFixed(3)} = {(1 - valuationResult.industry_info.private_discount_applied / 100).toFixed(3)}
                                    </p>
                                    <p className="font-mono text-gray-900 text-xs mt-2">
                                      Adjusted Value = ${Math.round(valuationResult.method_details.calculation_steps.final_calculation.enterprise_value).toLocaleString()} × {(1 - valuationResult.industry_info.private_discount_applied / 100).toFixed(3)}
                                    </p>
                                    <div className="mt-3 pt-3 border-t border-amber-200">
                                      <p className="font-bold text-green-700 text-lg">
                                        Final Value = ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Fallback if no detailed steps available */}
                        {!valuationResult.method_details?.calculation_steps && (
                          <div className="bg-white p-3 rounded border border-blue-100 space-y-1">
                            <p className="text-gray-900 font-semibold">Calculation:</p>
                            {methodInputs.dcf_perpetuity ? (
                              <>
                                <p className="text-gray-700 mt-2">Gordon Growth Model (Perpetuity):</p>
                                <p className="text-gray-900 font-semibold">PV = CF × (1 + g) / (r - g)</p>
                                <p className="text-green-700 font-bold mt-3">
                                  Total PV: ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-gray-700">Multi-period DCF + Terminal Value:</p>
                                <p className="text-gray-900 font-semibold mt-2">PV = Σ [CF_t / (1 + r)^t] + [TV / (1 + r)^n]</p>
                                <p className="text-green-700 font-bold mt-3">
                                  Total PV: ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                                </p>
                              </>
                            )}
                          </div>
                        )}

                        {/* Valuation Range */}
                        {valuationResult.low_range && valuationResult.high_range && (
                          <div className="bg-gray-50 p-3 rounded border border-gray-200">
                            <p className="font-semibold text-gray-900 mb-1">Final Valuation Range:</p>
                            <p className="text-green-700 font-bold">
                              ${Math.round(valuationResult.low_range).toLocaleString()} - ${Math.round(valuationResult.high_range).toLocaleString()}
                            </p>
                          </div>
                        )}

                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-green-900">Recommended Value:</span>
                            <span className="font-bold text-green-900 text-lg">
                              ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Manual Multiple Method */}
                    {valuationResult.selected_method === 'manual' && (
                      <div className="space-y-4">
                        {/* Input Parameters */}
                        <div className="bg-gradient-to-br from-indigo-100 to-purple-100 p-5 rounded-lg border-2 border-indigo-400 shadow-md">
                          <p className="text-base font-bold text-indigo-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Input Parameters
                          </p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Custom Multiple</p>
                              <p className="font-bold text-indigo-900">{manualMultiple}x</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Multiple Type</p>
                              <p className="font-bold text-indigo-900">{manualMultipleType === 'ev_ebitda' ? 'EV/EBITDA' : 'EV/Revenue'}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Financial Base</p>
                              <p className="font-bold text-indigo-900">
                                ${Math.round(manualMultipleType === 'ev_ebitda'
                                  ? valuationResult.financial_summary?.current_year?.ebitda
                                  : valuationResult.financial_summary?.current_year?.revenue || 0).toLocaleString()}
                              </p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                              <p className="font-bold text-amber-700">{valuationResult.industry_info?.private_discount_applied?.toFixed(0) || 0}%</p>
                            </div>
                          </div>
                        </div>

                        {/* Step 1: Calculate Base Value */}
                        <div className="bg-white p-4 rounded-lg border border-indigo-200 shadow-sm">
                          <p className="text-gray-900 font-semibold">Calculation:</p>
                          <p className="text-gray-700">Business Value = Financial Base × Multiple</p>
                          <p className="text-gray-900 font-semibold">
                            = ${Math.round(manualMultipleType === 'ev_ebitda'
                              ? valuationResult.financial_summary?.current_year?.ebitda
                              : valuationResult.financial_summary?.current_year?.revenue || 0).toLocaleString()} × {manualMultiple}
                          </p>
                          <p className="text-gray-900 font-semibold">
                            = ${Math.round((manualMultipleType === 'ev_ebitda'
                              ? valuationResult.financial_summary?.current_year?.ebitda
                              : valuationResult.financial_summary?.current_year?.revenue || 0) * parseFloat(manualMultiple)).toLocaleString()}
                          </p>
                          {valuationResult.industry_info && valuationResult.industry_info.private_discount_applied > 0 ? (
                            <>
                              <p className="text-amber-700 mt-2">
                                Apply {valuationResult.industry_info.private_discount_applied.toFixed(0)}% Private Company Discount:
                              </p>
                              <p className="text-gray-900 font-semibold">
                                = ${Math.round((manualMultipleType === 'ev_ebitda'
                                  ? valuationResult.financial_summary?.current_year?.ebitda
                                  : valuationResult.financial_summary?.current_year?.revenue || 0) * parseFloat(manualMultiple)).toLocaleString()} × {(1 - valuationResult.industry_info.private_discount_applied / 100).toFixed(2)}
                              </p>
                              <p className="text-green-700 font-bold mt-2">
                                Final Value: ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                              </p>
                            </>
                          ) : (
                            <p className="text-green-700 font-bold mt-2">
                              Final Value: ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                            </p>
                          )}
                        </div>

                        {/* Valuation Range */}
                        {valuationResult.low_range && valuationResult.high_range && (
                          <div className="bg-gray-50 p-3 rounded border border-gray-200">
                            <p className="font-semibold text-gray-900 mb-1">Final Valuation Range:</p>
                            <p className="text-green-700 font-bold">
                              ${Math.round(valuationResult.low_range).toLocaleString()} - ${Math.round(valuationResult.high_range).toLocaleString()}
                            </p>
                          </div>
                        )}

                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-green-900">Recommended Value:</span>
                            <span className="font-bold text-green-900 text-lg">
                              ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Capitalization of Earnings Method */}
                    {valuationResult.selected_method === 'capitalization' && (
                      <div className="space-y-4">
                        {/* Input Parameters */}
                        <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-5 rounded-lg border-2 border-purple-400 shadow-md">
                          <p className="text-base font-bold text-purple-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Input Parameters
                          </p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Earnings Metric</p>
                              <p className="font-bold text-purple-900">{capitalizationEarningsType === 'net_income' ? 'Net Income' : 'EBITDA'}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Earnings Amount</p>
                              <p className="font-bold text-purple-900">
                                ${Math.round(capitalizationEarningsType === 'net_income'
                                  ? (valuationResult.financial_summary?.current_year?.net_income || 0)
                                  : (valuationResult.financial_summary?.current_year?.ebitda || 0)).toLocaleString()}
                              </p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Capitalization Rate</p>
                              <p className="font-bold text-purple-900">{capitalizationRate ? capitalizationRate + '%' : 'Industry-Derived'}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                              <p className="font-bold text-amber-700">{valuationResult.industry_info?.private_discount_applied?.toFixed(0) || 0}%</p>
                            </div>
                          </div>
                        </div>

                        {/* Step 1: Calculate Base Value */}
                        <div className="bg-white p-4 rounded-lg border border-purple-200 shadow-sm">
                          <p className="text-gray-900 font-semibold">Calculation:</p>
                          <p className="text-gray-700">
                            Business Value = {capitalizationEarningsType === 'net_income' ? 'Net Income' : 'EBITDA'} ÷ Cap Rate
                          </p>
                          <p className="text-gray-900 font-semibold">
                            = ${Math.round(capitalizationEarningsType === 'net_income'
                              ? (valuationResult.financial_summary?.current_year?.net_income || 0)
                              : (valuationResult.financial_summary?.current_year?.ebitda || 0)).toLocaleString()} ÷ {capitalizationRate ? (parseFloat(capitalizationRate) / 100).toFixed(2) : '[Cap Rate]'}
                          </p>
                          {valuationResult.industry_info && valuationResult.industry_info.private_discount_applied > 0 ? (
                            <>
                              <p className="text-gray-900 font-semibold">
                                = ${Math.round(valuationResult.valuation_amount / (1 - valuationResult.industry_info.private_discount_applied / 100)).toLocaleString()}
                              </p>
                              <p className="text-amber-700 mt-2">
                                Apply {valuationResult.industry_info.private_discount_applied.toFixed(0)}% Private Company Discount:
                              </p>
                              <p className="text-gray-900 font-semibold">
                                = ${Math.round(valuationResult.valuation_amount / (1 - valuationResult.industry_info.private_discount_applied / 100)).toLocaleString()} × {(1 - valuationResult.industry_info.private_discount_applied / 100).toFixed(2)}
                              </p>
                              <p className="text-green-700 font-bold mt-2">
                                Final Value: ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                              </p>
                            </>
                          ) : (
                            <p className="text-green-700 font-bold mt-3">
                              = ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                            </p>
                          )}
                        </div>

                        {/* Valuation Range */}
                        {valuationResult.low_range && valuationResult.high_range && (
                          <div className="bg-gray-50 p-3 rounded border border-gray-200 mt-3">
                            <p className="font-semibold text-gray-900 mb-1">Final Valuation Range:</p>
                            <p className="text-green-700 font-bold">
                              ${Math.round(valuationResult.low_range).toLocaleString()} - ${Math.round(valuationResult.high_range).toLocaleString()}
                            </p>
                          </div>
                        )}

                        <div className="bg-green-50 p-3 rounded-lg border border-green-200 mt-3">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-green-900">Recommended Value:</span>
                            <span className="font-bold text-green-900 text-lg">
                              ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* NAV Method */}
                    {valuationResult.selected_method === 'nav' && (
                      <div className="space-y-4">
                        {/* Input Parameters */}
                        <div className="bg-gradient-to-br from-teal-100 to-cyan-100 p-5 rounded-lg border-2 border-teal-400 shadow-md">
                          <p className="text-base font-bold text-teal-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Input Parameters
                          </p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Total Assets</p>
                              <p className="font-bold text-teal-900">${Math.round(valuationResult.financial_summary?.current_year?.total_assets || financialData.total_assets?.[currentYear] || 0).toLocaleString()}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-xs text-gray-600 mb-1">Total Liabilities</p>
                              <p className="font-bold text-red-700">${Math.round(valuationResult.financial_summary?.current_year?.total_liabilities || financialData.total_liabilities?.[currentYear] || 0).toLocaleString()}</p>
                            </div>
                            <div className="bg-white/70 p-2 rounded col-span-2">
                              <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                              <p className="font-bold text-amber-700">{valuationResult.industry_info?.private_discount_applied?.toFixed(0) || 0}%</p>
                            </div>
                          </div>
                        </div>

                        {/* Step 1: Calculate Net Asset Value */}
                        <div className="bg-white p-4 rounded-lg border border-teal-200 shadow-sm">
                          <p className="text-gray-900 font-semibold">Calculation:</p>
                          <p className="text-gray-700">Net Asset Value = Assets - Liabilities</p>
                          <p className="text-gray-900 font-semibold">
                            = ${Math.round(valuationResult.financial_summary?.current_year?.total_assets || financialData.total_assets?.[currentYear] || 0).toLocaleString()} - ${Math.round(valuationResult.financial_summary?.current_year?.total_liabilities || financialData.total_liabilities?.[currentYear] || 0).toLocaleString()}
                          </p>
                          <p className="text-gray-900 font-semibold">
                            = ${Math.round((valuationResult.financial_summary?.current_year?.total_assets || financialData.total_assets?.[currentYear] || 0) - (valuationResult.financial_summary?.current_year?.total_liabilities || financialData.total_liabilities?.[currentYear] || 0)).toLocaleString()}
                          </p>
                          {valuationResult.industry_info && valuationResult.industry_info.private_discount_applied > 0 ? (
                            <>
                              <p className="text-amber-700 mt-2">
                                Apply {valuationResult.industry_info.private_discount_applied.toFixed(0)}% Private Company Discount:
                              </p>
                              <p className="text-gray-900 font-semibold">
                                = ${Math.round((valuationResult.financial_summary?.current_year?.total_assets || financialData.total_assets?.[currentYear] || 0) - (valuationResult.financial_summary?.current_year?.total_liabilities || financialData.total_liabilities?.[currentYear] || 0)).toLocaleString()} × {(1 - valuationResult.industry_info.private_discount_applied / 100).toFixed(2)}
                              </p>
                              <p className="text-green-700 font-bold mt-2">
                                Final Value: ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                              </p>
                            </>
                          ) : (
                            <p className="text-green-700 font-bold mt-2">
                              Final Value: ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                            </p>
                          )}
                        </div>

                        {/* Valuation Range */}
                        {valuationResult.low_range && valuationResult.high_range && (
                          <div className="bg-gray-50 p-3 rounded border border-gray-200">
                            <p className="font-semibold text-gray-900 mb-1">Final Valuation Range:</p>
                            <p className="text-green-700 font-bold">
                              ${Math.round(valuationResult.low_range).toLocaleString()} - ${Math.round(valuationResult.high_range).toLocaleString()}
                            </p>
                          </div>
                        )}

                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-green-900">Recommended Value:</span>
                            <span className="font-bold text-green-900 text-lg">
                              ${Math.round(valuationResult.valuation_amount).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Final Valuation Section */}
                <div className="bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 p-6 rounded-xl shadow-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-2xl text-white">Final Valuation</h4>
                  </div>
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg p-5 space-y-3">
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Valuation Range</span>
                      <span className="font-bold text-gray-900 text-xl">
                        ${valuationResult.low_range?.toLocaleString()} - ${valuationResult.high_range?.toLocaleString()}
                      </span>
                    </div>
                    {valuationResult.valuation_amount && (
                      <div className="flex justify-between items-center pt-2">
                        <span className="font-bold text-green-800 flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Recommended Value
                        </span>
                        <span className="font-bold text-green-700 text-2xl">
                          ${valuationResult.valuation_amount?.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowCalculationModal(false)}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors min-h-[44px]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedValuationModal;
